import type { ContactDrawerActivityItem } from "@/types/v2/conversation"

// Mock rows that `ActivityFeed` renders into its timeline
// while `loading`, so the shimmer library can measure the
// REAL row DOM and overlay a structure-matched shimmer while
// the contact-show `activity` prop is still deferring. The
// items are passed straight to the rows (filtered by the
// active chip) — not via `templateProps`. The values never
// reach the user; they exist only to give the feed a
// realistic shape (a few day-grouped rows of varying width)
// to measure, so the shimmer matches the loaded feed and
// nothing shifts when real data swaps in.
//
// A small spread of kinds + title lengths keeps the
// measured rows from all being identical width and covers
// the main chips (message / call / update / note). For a
// small count, switching to a chip the generated cycle
// didn't reach can briefly show the empty state — that's
// fine, it mirrors what the (also-small) real feed shows
// for that filter.
const SHIMMER_TEMPLATE: ContactDrawerActivityItem[] = [
  {
    id: "shimmer-1",
    kind: "message",
    actor: { name: "Maria Lopez", initials: "ML", kind: "contact" },
    title: "Maria Lopez · message in",
    body: "Hi, do you have time to chat about the proposal?",
    direction: "inbound",
    dealId: null,
    dealName: null,
    occurredAt: "2026-01-01T12:00:00Z",
    occurredAtIso: "2026-01-01T12:00:00Z",
  },
  {
    id: "shimmer-2",
    kind: "call",
    actor: { name: "David Aguilar", initials: "DA", kind: "employee" },
    title: "Outbound call",
    body: null,
    direction: "outbound",
    outcome: "connected",
    durationLabel: "4m 12s",
    dealId: null,
    dealName: null,
    occurredAt: "2026-01-01T11:30:00Z",
    occurredAtIso: "2026-01-01T11:30:00Z",
  },
  {
    id: "shimmer-3",
    kind: "stage",
    actor: { name: "David Aguilar", initials: "DA", kind: "employee" },
    title: "Moved to Negotiation",
    body: null,
    dealId: 1,
    dealName: "Acme renewal",
    occurredAt: "2026-01-01T10:00:00Z",
    occurredAtIso: "2026-01-01T10:00:00Z",
  },
  {
    id: "shimmer-4",
    kind: "note",
    actor: { name: "David Aguilar", initials: "DA", kind: "employee" },
    title: "Note",
    body: "Following up next week after they review internally.",
    dealId: 1,
    dealName: "Acme renewal",
    occurredAt: "2025-12-31T16:00:00Z",
    occurredAtIso: "2025-12-31T16:00:00Z",
  },
]

// Build exactly `count` mock rows by cycling the template,
// so the loading shimmer renders the SAME number of rows the
// real (deferred) feed will — the count is shipped eagerly
// by the server. Matching the length is what prevents the
// layout shift when real data swaps in (per the
// shimmer-from-structure guidance). Ids are unique so React
// keys stay stable. `count` is already capped server-side to
// the feed's row limit; we clamp again defensively.
export function buildActivityShimmerItems(
  count: number,
): ContactDrawerActivityItem[] {
  const n = Math.max(0, Math.min(count, 1000))
  return Array.from({ length: n }, (_, i) => {
    const base = SHIMMER_TEMPLATE[i % SHIMMER_TEMPLATE.length]
    return { ...base, id: `shimmer-${i}` }
  })
}
